import React, { Fragment, useEffect, useState } from 'react';
import {
    Row, Col, Typography, Table, Button, Input, Modal,
    Form, Flex, Popconfirm, Space,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getActivities, createActivity, updateActivity, removeActivity } from './redux/slice';

const { Title, Text } = Typography;

const SupportTaetigkeitenPage = () => {
    const dispatch = useDispatch();
    const { auth, supportTracking } = useSelector(s => s);
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const { activities, activityLoading } = supportTracking;
    const organizer = auth?.decodeToken?.organizer;

    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);

    useEffect(() => {
        if (organizer) dispatch(getActivities({ organizer, search: '' }));
    }, [organizer]);

    const handleSearch = () => {
        dispatch(getActivities({ organizer, search }));
    };

    const openAdd = () => {
        setEditRecord(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditRecord(record);
        form.setFieldsValue({ name: record.name });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editRecord) {
                const result = await dispatch(updateActivity({ id: editRecord._id, name: values.name, organizer }));
                if (result?.payload) setModalOpen(false);
            } else {
                const result = await dispatch(createActivity({ name: values.name, organizer }));
                if (result?.payload) setModalOpen(false);
            }
        } catch (_) {}
    };

    const columns = [
        {
            title: t('support_taetigkeit_name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size='small' onClick={() => openEdit(record)} />
                    <Popconfirm
                        title={t('support_delete_confirm')}
                        onConfirm={() => dispatch(removeActivity({ id: record._id, organizer }))}
                        okText={t('organizer_common_yes') || 'Ja'}
                        cancelText={t('organizer_common_cancelButton')}
                    >
                        <Button icon={<DeleteOutlined />} size='small' danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Fragment>
            <Flex vertical gap={16}>
                <Row justify='space-between' align='middle' className='padding-1 rounded-1' gutter={[16, 16]}>
                    <Col>
                        <Title level={4} type='success'>{t('support_taetigkeiten_title')}</Title>
                    </Col>
                    <Col>
                        <Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>
                            {t('support_taetigkeit_hinzufuegen')}
                        </Button>
                    </Col>
                </Row>

                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder={t('organizer_common_search')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onPressEnter={handleSearch}
                            allowClear
                        />
                    </Col>
                    <Col>
                        <Button type='primary' onClick={handleSearch}>{t('organizer_common_search')}</Button>
                    </Col>
                </Row>

                <div className='bg-white rounded-2'>
                    <Table
                        dataSource={activities}
                        columns={columns}
                        rowKey='_id'
                        loading={activityLoading}
                        pagination={{ pageSize: 20 }}
                    />
                </div>
            </Flex>

            <Modal
                open={modalOpen}
                title={
                    <Title level={4} type='success'>
                        {editRecord ? t('support_taetigkeit_bearbeiten') : t('support_taetigkeit_hinzufuegen')}
                    </Title>
                }
                footer={null}
                onCancel={() => setModalOpen(false)}
            >
                <Form
                    form={form}
                    layout='vertical'
                    requiredMark={(label, rule) =>
                        <Text>{label}<Text type='danger'>{rule?.required && ' *'}</Text></Text>
                    }
                >
                    <Form.Item
                        name='name'
                        label={t('support_taetigkeit_name')}
                        rules={[{ required: true, message: t('support_taetigkeit_name') + ' ' + t('organizer_common_isrequired') }]}
                    >
                        <Input placeholder='z.B. Fernwartung' />
                    </Form.Item>

                    <Row justify='end' gutter={8}>
                        <Col>
                            <Button onClick={() => setModalOpen(false)}>{t('organizer_common_cancelButton')}</Button>
                        </Col>
                        <Col>
                            <Button type='primary' loading={activityLoading} onClick={handleSave}>
                                {t('organizer_common_saveButton') || 'Speichern'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Fragment>
    );
};

export default SupportTaetigkeitenPage;
